import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CopyBox } from "@/components/CopyBox";
import { SERVICE_LANES } from "@/config/resources";
import { Plus, Code2, Inbox, Trash2, FormInput, Edit3 } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const lanes = (SERVICE_LANES || []).map((l) => (typeof l === "string" ? { value: l, label: l } : l));
const blankForm = { name: "", site_id: "", service_interest: "", create_lead: true, create_subscriber: false, success_message: "Thank you! We'll be in touch shortly.", redirect_url: "", submit_label: "Request a free consultation" };

export default function MarketingForms() {
  const [forms, setForms] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [embed, setEmbed] = useState(null);
  const [subs, setSubs] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, s] = await Promise.all([api.get("/marketing/forms"), api.get("/marketing/sites")]);
      setForms(f.data.items || []); setSites(s.data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editor.name.trim()) return toast.error("Form name is required");
    try {
      const payload = { ...editor, site_id: editor.site_id || null, service_interest: editor.service_interest || null };
      if (editor.id) await api.put(`/marketing/forms/${editor.id}`, payload);
      else await api.post("/marketing/forms", payload);
      toast.success("Form saved"); setEditor(null); load();
    } catch { toast.error("Could not save form"); }
  };

  const remove = async (f) => { await api.delete(`/marketing/forms/${f.id}`); toast.success("Deleted"); load(); };

  const openEmbed = async (f) => {
    try { const { data } = await api.get(`/marketing/forms/${f.id}/embed`, { params: { base: BACKEND } }); setEmbed({ form: f, ...data }); }
    catch { toast.error("Could not load embed code"); }
  };
  const openSubs = async (f) => {
    try { const { data } = await api.get(`/marketing/forms/${f.id}/submissions`); setSubs({ form: f, items: data.items || [] }); }
    catch { toast.error("Could not load submissions"); }
  };

  return (
    <div className="space-y-6" data-testid="marketing-forms">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#252221]">Landing-Page Forms</h1>
          <p className="text-sm text-muted-foreground">Embeddable forms that capture website leads straight into your pipeline.</p>
        </div>
        <Button onClick={() => setEditor({ ...blankForm })} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="new-form-button"><Plus size={15} className="mr-1.5" /> New form</Button>
      </div>

      {loading ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{[...Array(2)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div> :
        forms.length === 0 ? (
          <Card className="p-12 text-center border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="forms-empty">
            <FormInput size={26} className="mx-auto text-[#1C4358] mb-3" />
            <p className="font-medium text-[#252221]">No forms yet</p>
            <p className="text-sm text-muted-foreground">Create a form, then copy its embed code onto your website.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {forms.map((f) => (
              <Card key={f.id} className="p-5 border-[#E6EDF2]" style={{ boxShadow: "var(--mmm-shadow-sm)" }} data-testid="form-card">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display font-semibold text-[#252221] truncate">{f.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{sites.find((s) => s.id === f.site_id)?.name || "No site"} · {f.submit_count || 0} submissions</div>
                  </div>
                  <StatusBadge value={f.is_active ? "active" : "inactive"} />
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => openEmbed(f)} data-testid="form-embed-button"><Code2 size={14} className="mr-1.5" /> Embed code</Button>
                  <Button size="sm" variant="outline" onClick={() => openSubs(f)} data-testid="form-submissions-button"><Inbox size={14} className="mr-1.5" /> Submissions</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditor({ ...blankForm, ...f })} data-testid="form-edit-button"><Edit3 size={14} className="mr-1.5" /> Edit</Button>
                  <button onClick={() => remove(f)} className="p-2 rounded-lg text-[#9C1E22] hover:bg-[rgba(156,30,34,0.08)] ml-auto" data-testid="form-delete-button" aria-label="Delete"><Trash2 size={15} /></button>
                </div>
              </Card>
            ))}
          </div>
        )}

      {/* Editor */}
      <Dialog open={!!editor} onOpenChange={(o) => !o && setEditor(null)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto mmm-scroll" data-testid="form-editor-dialog">
          <DialogHeader><DialogTitle>{editor?.id ? "Edit form" : "New form"}</DialogTitle></DialogHeader>
          {editor && (
            <div className="space-y-3">
              <div><Label className="text-sm">Form name</Label><Input value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} placeholder="Free Consultation" data-testid="form-name-input" /></div>
              <div><Label className="text-sm">Website</Label>
                <Select value={editor.site_id || "none"} onValueChange={(v) => setEditor({ ...editor, site_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="form-site-select"><SelectValue placeholder="Select a site" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No site</SelectItem>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">Service interest</Label>
                <Select value={editor.service_interest || "none"} onValueChange={(v) => setEditor({ ...editor, service_interest: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="form-lane-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Not specified</SelectItem>{lanes.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">Submit button label</Label><Input value={editor.submit_label} onChange={(e) => setEditor({ ...editor, submit_label: e.target.value })} data-testid="form-submit-label" /></div>
              <div><Label className="text-sm">Success message</Label><Textarea rows={2} value={editor.success_message} onChange={(e) => setEditor({ ...editor, success_message: e.target.value })} data-testid="form-success-msg" /></div>
              <div><Label className="text-sm">Redirect URL (optional)</Label><Input value={editor.redirect_url || ""} onChange={(e) => setEditor({ ...editor, redirect_url: e.target.value })} placeholder="https://..." data-testid="form-redirect" /></div>
              <div className="flex items-center justify-between rounded-lg border border-[#E6EDF2] p-3"><div><div className="text-sm font-medium">Create a lead on submit</div><div className="text-xs text-muted-foreground">Adds the submission to your pipeline</div></div><Switch checked={editor.create_lead} onCheckedChange={(v) => setEditor({ ...editor, create_lead: v })} data-testid="form-create-lead" /></div>
              <div className="flex items-center justify-between rounded-lg border border-[#E6EDF2] p-3"><div><div className="text-sm font-medium">Add to newsletter list</div><div className="text-xs text-muted-foreground">Subscribe the contact's email</div></div><Switch checked={editor.create_subscriber} onCheckedChange={(v) => setEditor({ ...editor, create_subscriber: v })} data-testid="form-create-subscriber" /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditor(null)}>Cancel</Button><Button onClick={save} className="text-white" style={{ backgroundColor: "var(--mmm-red)" }} data-testid="form-save-button">Save form</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Embed */}
      <Dialog open={!!embed} onOpenChange={(o) => !o && setEmbed(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="form-embed-dialog">
          <DialogHeader><DialogTitle>Embed “{embed?.form?.name}”</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Paste this HTML where you want the form to appear on your website.</p>
          <CopyBox value={embed?.snippet || ""} testid="form-embed-copy" />
        </DialogContent>
      </Dialog>

      {/* Submissions */}
      <Dialog open={!!subs} onOpenChange={(o) => !o && setSubs(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto mmm-scroll" data-testid="form-submissions-dialog">
          <DialogHeader><DialogTitle>Submissions · {subs?.form?.name}</DialogTitle></DialogHeader>
          {subs?.items?.length === 0 ? <p className="text-sm text-muted-foreground py-8 text-center">No submissions yet.</p> : (
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Date</TableHead><TableHead>Lead</TableHead></TableRow></TableHeader>
              <TableBody>
                {(subs?.items || []).map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="font-medium text-[#252221]">{s.data?.name || "—"}</TableCell>
                    <TableCell className="text-sm">{s.data?.email || "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(s.created_at)}</TableCell>
                    <TableCell>{s.lead_id ? <StatusBadge value="created" /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
